import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import AmCharts from '@amcharts/amcharts3-react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

const colors = [
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#00FFFF",
    "#FF00FF",
    "#FFFF00",
    "#808000",
    "#800000",
    "#008080",
    "#000080"
];

const bigNumberPrefixes = [
    { "number": 1e+3, "prefix": "k" },
    { "number": 1e+6, "prefix": "M" },
    { "number": 1e+9, "prefix": "G" },
    { "number": 1e+12, "prefix": "T" },
    { "number": 1e+15, "prefix": "P" },
    { "number": 1e+18, "prefix": "E" },
    { "number": 1e+21, "prefix": "Z" },
    { "number": 1e+24, "prefix": "Y" }
];

class QueryChart extends PureComponent {
    getTimestampBoundries = ({ date }) => {
        let start = moment.utc(date).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            end = moment.utc(date).endOf('day').format('YYYY-MM-DD HH:mm:ss');

        this.props.constructDrillDownRequest(start, end);
    };

    generateQueryParams = (paramIndex) => {
        const { queryParams } = this.props;
        return queryParams[paramIndex] ? `${queryParams[paramIndex].key}: ${queryParams[paramIndex].value}` : ``;
    };

    generateGraphs = () => {
        let chartAttributes = [...this.props.chartAttributes];
        if (chartAttributes.length) {
            return chartAttributes.map((attribute, id) => {
                return {
                    "valueAxis": "v"+id.toString(),
                    "lineColor": colors[id],
                    "bullet": "round",
                    "bulletBorderThickness": 1,
                    "hideBulletsCount": 30,
                    "title": attribute,
                    "valueField": attribute,
                    "fillAlphas": 0
                }
            });
        } else {
            return [{
                "id": "g1",
                "balloon": {
                    "adjustBorderColor": true,
                    "verticalPadding": 3,
                    "color": "#ffffff",
                    "fillColor": "#258cbb",
                    "borderColor": "#258cbb"
                },
                "bullet": "round",
                "bulletBorderAlpha": 1,
                "bulletColor": "#FFFFFF",
                "bulletSize": this.props.showBullets ? 12 : 5,
                "lineThickness": 2,
                "title": "red line",
                "useLineColorForBulletBorder": true,
                "balloonText": "<span style='font-size:14px;'>[[value]]</span>",
                "valueField": "value",
                "bulletHitAreaSize": 20,
                // if combination of [min|max] && by minute -> always show bullets
                // all other combinations -> show bullets only if data points < 30
                //"hideBulletsCount": showBullets ? 0 : 30
            }];
        }
    };

    generateValueAxes = () => {
        let chartAttributes = [...this.props.chartAttributes];
        if (chartAttributes.length) {
            return chartAttributes.map((attribute, id) => {
                return {
                    /*"id": "v"+id.toString(),*/
                    "axisColor": colors[id],
                    "axisThickness": 2,
                    "axisAlpha": 1,
                    "position": id % 2 === 0 ? "left" : "right",
                    "offset": id * 20,
                    "usePrefixes": true,
                    "prefixesOfBigNumbers": bigNumberPrefixes
                }
            });
        } else {
            return [{
                "autoWrap": true,
                "position": "left",
                "usePrefixes": true,
                "prefixesOfBigNumbers": bigNumberPrefixes
            }]
        }
    };

    generateChartCursor = () => {
        let chartAttributes = [...this.props.chartAttributes];
        if (chartAttributes.length) {
            return {
                "cursorPosition": "mouse"
            }
        } else {
            return {
                "categoryBalloonDateFormat": this.props.chartCursorFormat,
                "cursorPosition": "mouse",
                "pan": true,
                "valueLineEnabled": true,
                "valueLineBalloonEnabled": false,
                "cursorAlpha": 1,
                "cursorColor": "#258cbb",
                "limitToGraph": "g1",
                "valueLineAlpha": 0.2,
                "valueZoomable": true
            }
        }
    };

    generateChartScrollbar = () => {
        return {
            /*"graph": "g1",*/
            "scrollbarHeight": 80,
            "backgroundAlpha": 0,
            "selectedBackgroundAlpha": 0.1,
            "selectedBackgroundColor": "#888888",
            "graphFillAlpha": 0,
            "graphLineAlpha": 0.5,
            "selectedGraphFillAlpha": 0,
            "selectedGraphLineAlpha": 1,
            "autoGridCount": false,
            "gridCount": 0,
            "color": "#AAAAAA",
            "oppositeAxis": false,
        };
    };

    generateOptions = () => {
        const self = this;
        const {
            chartData, chartCursorFormat, minPeriod, dataGroupName,
            startDate, endDate, chartAttributes } = this.props;

        let options = {
            "type": "serial",
            "theme": "light",
            "autoMarginOffset": 30,
            "marginRight": 80,
            "marginTop": 60,
            "balloon": {
                "borderThickness": 1,
                "shadowAlpha": 0
            },
            "listeners": [{
                "event": "rendered",
                "method": function (e) {
                    e.chart.valueAxes[0].zoomOut();
                }
            },
                {
                    "event": "clickGraphItem",
                    "method": function (event) {
                        if (self.props.chosenCategory === 'day') {
                            self.getTimestampBoundries(event.item.dataContext);
                        }
                    }
                }
            ],
            "valueScrollbar": {
                "oppositeAxis": false,
                "offset": 50,
                "scrollbarHeight": 10
            },
            "categoryField": "date",
            "categoryAxis": {
                "startOnAxis": false,
                "autoRotateAngle": 30,
                "autoRotateCount": 10,
                "parseDates": false,
                "equalSpacing": true,
                "minPeriod": minPeriod,
                "categoryFunction": function (valueText, data, categoryAxis) {
                    // On hover labels
                    if (data.dataPeriods != null && data.dataPeriods.Start != null && data.dataPeriods.End != null) {
                        return "From: " + moment(data.dataPeriods.Start, 'YYYY/MM/DD').format(chartCursorFormat.replace("JJ:NN", "HH:mm")) + "\n"
                            + "To: " + moment(data.dataPeriods.End, 'YYYY/MM/DD').format(chartCursorFormat.replace("JJ:NN", "HH:mm"));
                    } else {
                        return moment(data.date).format(chartCursorFormat);
                    }
                },
                "labelFunction": function (valueText, data, categoryAxis) {
                    // X axis labels
                    if (data.dataContext && data.dataContext.currentPeriod != null) {
                        return data.dataContext.currentPeriod;
                    } else {
                        return data.category;
                    }
                }
            },
            "export": {
                "enabled": true,
                "pageOrientation": "landscape",
                "pageSize": "A3",
                "dateFormat": "YYYY-MM-DD HH:NN:SS",
                "menu": [{
                    "class": "export-main",
                    "menu": [{
                        "label": "  Export as  ",
                        "menu": [{
                            "label": "PDF",
                            "format": "PDF",
                            "click": function () {
                                this.capture({}, function () {
                                    let content = ['Saved from: ',
                                        window.location.href,
                                        ` `,
                                        `Data Group: ${dataGroupName}`,
                                        `Start Date: ${moment(startDate).format('YYYY-MM-DD HH:mm:ss')}`,
                                        `End Date: ${moment(endDate).format('YYYY-MM-DD HH:mm:ss')}`,
                                        ` `,
                                        `Query Parameters: `,
                                        `${self.generateQueryParams(0)}`,
                                        `${self.generateQueryParams(1)}`,
                                        `${self.generateQueryParams(2)}`, {
                                            image: "reference",
                                            fit: [1190.55, 1683.78]
                                        }];
                                    this.toPDF({
                                        content: content
                                    }, function (data) {
                                        this.download(data, "application/pdf", "amCharts.pdf");
                                    });
                                })
                            }
                        }]
                    }]
                }]
            },
            "dataProvider": chartData,
            "synchronizeGrid": chartAttributes.length > 0,
            "valueAxes": this.generateValueAxes(),
            "graphs": this.generateGraphs(),
            "chartCursor": this.generateChartCursor(),
            "chartScrollbar": this.generateChartScrollbar()
        };

        if (chartAttributes.length > 0) {
            options['legend'] = {
                "useGraphSettings": true
            }
        }

        return options;
    };

    render() {
        const {queryParams} = this.props;
        if (isEmpty(queryParams))
            return null;
        const config = this.generateOptions();

        return (
            <AmCharts.React
                style={{
                    height: "500px",
                    padding: 0
                }}
                options={config}
            />
        );
    }
}

QueryChart.propTypes = {
    chartData: PropTypes.array,
    chartCursorFormat: PropTypes.string,
    minPeriod: PropTypes.string,
    showBullets: PropTypes.bool,
    chosenCategory: PropTypes.string,
    constructDrillDownRequest: PropTypes.func,
    dataGroupName: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    queryParams: PropTypes.array,
    chartAttributes: PropTypes.array
};

export default QueryChart;


// WEBPACK FOOTER //
// ./src/main/content/query/QueryChart.js